import React, { useState, useEffect } from 'react';
import api from '../api/axiosClient';
import { FaExclamationTriangle, FaCheckCircle, FaPaperPlane } from 'react-icons/fa';

const TYPE_OPTIONS = [
  { value: 'Dispute',   label: 'Dispute',        hint: "The job wasn't done as agreed" },
  { value: 'Complaint', label: 'Complaint',       hint: 'Behavior, quality, or overcharging' },
  { value: 'Payment',   label: 'Payment issue',   hint: "Money was charged/paid but doesn't match" },
  { value: 'Help',      label: 'General help',    hint: "Login trouble, account issues, or anything else" },
];

export default function RaiseIssueForm({ jobs = [], isAuthenticated = true, onSent }) {
  const guestOptions = TYPE_OPTIONS.filter(o => o.value === 'Help');
  const typeOptions = isAuthenticated ? TYPE_OPTIONS : guestOptions;

  const [type, setType]         = useState(isAuthenticated ? 'Dispute' : 'Help');
  const [jobId, setJobId]       = useState('');
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [subject, setSubject]   = useState('');
  const [message, setMessage]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState(null);
  const [sent, setSent]         = useState(false);

  // "General help" doesn't need a job attached; other types do, if the
  // user has any jobs to pick from.
  const jobRequired = isAuthenticated && type !== 'Help' && jobs.length > 0;

  useEffect(() => {
    // Reset job selection if the type changes to one that doesn't need it
    if (!jobRequired) setJobId('');
  }, [jobRequired]);

  const emailValid = !isAuthenticated ? /^\S+@\S+\.\S+$/.test(email.trim()) : true;
  const nameValid = !isAuthenticated ? name.trim().length > 0 : true;

  const canSubmit =
    subject.trim() && message.trim() &&
    (!jobRequired || jobId) &&
    emailValid && nameValid &&
    (isAuthenticated || (email.trim() && name.trim())) &&
    !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        type,
        subject: subject.trim(),
        message: message.trim(),
      };
      if (jobId) payload.related_job = Number(jobId);
      if (!isAuthenticated) {
        payload.name = name.trim();
        payload.email = email.trim();
      }

      const { data } = await api.post('/support-tickets/raise/', payload);

      setSent(true);
      setSubject('');
      setMessage('');
      setJobId('');
      setName('');
      setEmail('');
      onSent?.(data);
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        (data && (data.name?.[0] || data.related_job?.[0] || data.message?.[0] || data.email?.[0] || data.detail || data.error)) ||
        err.message ||
        'Could not submit your issue. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = {
    fontSize: '.68rem', fontWeight: 700, color: '#8a8a8a', textTransform: 'uppercase',
    letterSpacing: '.06em', display: 'block', marginBottom: 8,
  };

  const inputStyle = {
    width: '100%', background: '#fff', border: '1.5px solid #e6e6e6',
    borderRadius: 9, padding: '.65rem .9rem', color: '#000', fontSize: '.86rem', outline: 'none',
  };

  if (sent) {
    return (
      <div style={{
        background: '#f7fbf7', border: '1.5px solid #c8e6c9',
        borderRadius: 14, padding: '2rem', textAlign: 'center',
        height: '100%', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <FaCheckCircle style={{ fontSize: '2rem', color: '#22c55e', marginBottom: 10 }} />
        <p style={{ color: '#1a1a1a', fontWeight: 700, marginBottom: 4 }}>Issue submitted</p>
        <p style={{ color: '#6c757d', fontSize: '.85rem', marginBottom: 16 }}>
          {isAuthenticated
            ? 'Our support team will review it and get back to you.'
            : "Our support team will review it and email you a reply at the address you provided."}
        </p>
        <button
          onClick={() => setSent(false)}
          style={{
            background: '#fff', border: '1.5px solid #e0e0e0',
            color: '#333', borderRadius: 9, padding: '.55rem 1.1rem', fontSize: '.82rem',
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          Report another issue
        </button>
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff', border: '1.5px solid #ede9e2',
      borderRadius: 14, padding: '1.5rem', boxShadow: '0 2px 16px rgba(0,0,0,.06)',
      height: '100%', display: 'flex', flexDirection: 'column',
    }}>
      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 10, padding: '.7rem 1rem', marginBottom: '1.1rem',
          color: '#dc2626', fontSize: '.83rem', display: 'flex', gap: 8, alignItems: 'flex-start',
        }}>
          <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0 }} size={13} />
          <span>{error}</span>
        </div>
      )}

      {/* Type selector */}
      {typeOptions.length > 1 && (
        <>
          <label style={labelStyle}>
            What's this about?
          </label>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 8, marginBottom: '1.1rem',
          }}>
            {typeOptions.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setType(opt.value)}
                style={{
                  textAlign: 'left', borderRadius: 10, padding: '.7rem .8rem', cursor: 'pointer',
                  border: `1.5px solid ${type === opt.value ? '#e6c200' : '#e6e6e6'}`,
                  background: type === opt.value ? '#fffde0' : '#fafafa',
                  transition: 'all .15s',
                }}
              >
                <div style={{
                  fontSize: '.84rem', fontWeight: 700,
                  color: type === opt.value ? '#8a7600' : '#1a1a1a',
                }}>
                  {opt.label}
                </div>
                <div style={{ fontSize: '.72rem', color: '#6c757d', marginTop: 2, lineHeight: 1.3 }}>
                  {opt.hint}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Job picker — shown whenever the user has jobs and the type isn't "Help" */}
      {isAuthenticated && jobs.length > 0 && type !== 'Help' && (
        <div style={{ marginBottom: '1.1rem' }}>
          <label style={labelStyle}>
            Which job is this about?
          </label>
          <select
            value={jobId}
            onChange={e => setJobId(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value="">Select a job…</option>
            {jobs.map(j => (
              <option key={j.id} value={j.id}>
                #{j.id} · {j.service}{j.name ? ` — ${j.name}` : j.client_name ? ` — ${j.client_name}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Name — guests only, required by the backend for anonymous tickets */}
      {!isAuthenticated && (
        <div style={{ marginBottom: '1.1rem' }}>
          <label style={labelStyle}>
            Your name
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Jane Doe"
            style={inputStyle}
          />
        </div>
      )}

      {/* Email — guests only, so support has a way to reply without an account */}
      {!isAuthenticated && (
        <div style={{ marginBottom: '1.1rem' }}>
          <label style={labelStyle}>
            Your email
          </label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            style={inputStyle}
          />
          <p style={{ fontSize: '.72rem', color: '#6c757d', marginTop: 6 }}>
            We'll reply here since you're not signed in.
          </p>
        </div>
      )}

      {/* Subject */}
      <div style={{ marginBottom: '1.1rem' }}>
        <label style={labelStyle}>
          Subject
        </label>
        <input
          value={subject}
          onChange={e => setSubject(e.target.value)}
          placeholder={isAuthenticated ? "Short summary, e.g. 'Tiling job left half-finished'" : "Short summary, e.g. 'Can't log in to my account'"}
          style={inputStyle}
        />
      </div>

      {/* Message — grows to absorb any leftover column height so the card
          matches the FAQ accordion's height instead of leaving a gap */}
      <div style={{ marginBottom: '1.25rem', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <label style={labelStyle}>
          Details
        </label>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Describe what happened, with dates and specifics if you can…"
          style={{
            ...inputStyle, padding: '.75rem .9rem', resize: 'none', fontFamily: 'inherit',
            flex: 1, minHeight: 100,
          }}
        />
      </div>

      <button
        onClick={submit}
        disabled={!canSubmit}
        style={{
          background: canSubmit ? '#fff000' : '#f0f0f0',
          color: canSubmit ? '#000' : '#999',
          border: 'none', borderRadius: 10, padding: '.75rem 1.5rem',
          fontWeight: 700, fontSize: '.86rem', cursor: canSubmit ? 'pointer' : 'default',
          display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center',
          boxShadow: canSubmit ? '0 4px 14px rgba(255,240,0,.35)' : 'none',
          transition: 'all .18s', flexShrink: 0,
        }}
      >
        <FaPaperPlane size={12} /> {submitting ? 'Submitting…' : 'Submit Issue'}
      </button>
    </div>
  );
}
